import { http } from '../lib/api/http';
import { searchCompaniesByName } from './companyApi';
import { getCompanyPortalPath } from '../constants/companyRoutes';

const DEFAULT_LIMIT_PER_TYPE = 4;

const QUICK_ACCESS_LINKS = [
  {
    id: 'quick-access-dashboard-feed',
    type: 'section',
    categoryLabel: 'Dashboard',
    title: 'Feed',
    subtitle: 'Actividad reciente y novedades',
    href: '/dashboard?section=feed',
    icon: 'feed',
    keywords: ['feed', 'dashboard', 'actividad', 'inicio'],
  },
  {
    id: 'quick-access-dashboard-solicitudes-mapa',
    type: 'marketplace',
    categoryLabel: 'Marketplace',
    title: 'Solicitudes del marketplace',
    subtitle: 'Explora oportunidades activas de clientes',
    href: '/marketplace?segment=requests',
    icon: 'requests',
    keywords: ['solicitudes', 'oportunidades', 'marketplace'],
  },
  {
    id: 'quick-access-dashboard-solicitudes',
    type: 'section',
    categoryLabel: 'Dashboard',
    title: 'Centro de solicitudes',
    subtitle: 'Gestiona solicitudes entrantes',
    href: '/dashboard?section=solicitudes',
    icon: 'solicitudes',
    keywords: ['solicitudes', 'oportunidades', 'dashboard'],
  },
  {
    id: 'quick-access-dashboard-reservas',
    type: 'section',
    categoryLabel: 'Dashboard',
    title: 'Reservas',
    subtitle: 'Controla tus reservas y calendarizaciones',
    href: '/dashboard?section=reservas',
    icon: 'reservas',
    keywords: ['reservas', 'agenda', 'calendario'],
  },
  {
    id: 'quick-access-dashboard-unidades',
    type: 'section',
    categoryLabel: 'Dashboard',
    title: 'Flotilla',
    subtitle: 'Administra tus unidades registradas',
    href: '/dashboard?section=unidades',
    icon: 'unidades',
    keywords: ['flotilla', 'unidades', 'vehiculos'],
  },
  {
    id: 'quick-access-dashboard-publicaciones',
    type: 'section',
    categoryLabel: 'Dashboard',
    title: 'Publicaciones',
    subtitle: 'Edita y publica inventario',
    href: '/dashboard?section=publicaciones',
    icon: 'publicaciones',
    keywords: ['publicaciones', 'listados', 'inventario'],
  },
  {
    id: 'quick-access-dashboard-promocionar',
    type: 'section',
    categoryLabel: 'Dashboard',
    title: 'Promocionar',
    subtitle: 'Impulsa la visibilidad de tus unidades',
    href: '/dashboard?section=promocionar',
    icon: 'promocionar',
    keywords: ['promocionar', 'promociones', 'visibilidad'],
  },
  {
    id: 'quick-access-dashboard-empresa',
    type: 'section',
    categoryLabel: 'Dashboard',
    title: 'Empresa',
    subtitle: 'Actualiza la informacion de tu empresa',
    href: () => getCompanyPortalPath(),
    icon: 'empresa',
    keywords: ['empresa', 'perfil', 'datos', 'organizacion'],
  },
  {
    id: 'quick-access-dashboard-calendario',
    type: 'section',
    categoryLabel: 'Dashboard',
    title: 'Calendario',
    subtitle: 'Consulta tus eventos y reservas',
    href: '/dashboard?section=calendario',
    icon: 'calendario',
    keywords: ['calendario', 'agenda', 'reservas'],
  },
  {
    id: 'quick-access-dashboard-support',
    type: 'section',
    categoryLabel: 'Dashboard',
    title: 'Soporte',
    subtitle: 'Contacta al equipo de soporte',
    href: '/dashboard?section=support',
    icon: 'support',
    keywords: ['soporte', 'ayuda', 'tickets'],
  },
  {
    id: 'quick-access-dashboard-suscripciones',
    type: 'section',
    categoryLabel: 'Dashboard',
    title: 'Suscripciones',
    subtitle: 'Gestiona tus planes y beneficios',
    href: '/dashboard?section=suscripciones',
    icon: 'suscripciones',
    keywords: ['suscripciones', 'planes', 'membresia'],
  },
  {
    id: 'quick-link-marketplace',
    type: 'section',
    categoryLabel: 'Navegacion',
    title: 'Marketplace',
    subtitle: 'Explora unidades y publicaciones activas',
    href: '/marketplace',
    icon: 'marketplace',
    keywords: ['marketplace', 'catalogo', 'publicaciones', 'market'],
  },
  {
    id: 'quick-link-requests',
    type: 'section',
    categoryLabel: 'Navegacion',
    title: 'Solicitudes publicadas',
    subtitle: 'Oportunidades recientes de transporte',
    href: '/solicitudes',
    icon: 'requests',
    keywords: ['solicitudes', 'oportunidades', 'publicadas'],
  },
];

const QUICK_ACCESS_MAP = QUICK_ACCESS_LINKS.reduce((acc, link) => {
  acc.set(link.id, link);
  return acc;
}, new Map());

const DEFAULT_QUICK_ACCESS_IDS = [
  'quick-access-dashboard-feed',
  'quick-access-dashboard-reservas',
  'quick-access-dashboard-publicaciones',
  'quick-link-marketplace',
  'quick-link-requests',
];

const mapSuggestion = (record, overrides = {}) => {
  const hrefValue = typeof record.href === 'function' ? record.href() : record.href;
  return {
    id: record.id,
    type: record.type,
    categoryLabel: record.categoryLabel,
    title: record.title,
    subtitle: record.subtitle || '',
    href: hrefValue,
    icon: record.icon || null,
    searchValue: record.searchValue,
    isPlaceholder: Boolean(record.isPlaceholder),
    ...overrides,
  };
};

const mapQuickAccessSuggestion = link => mapSuggestion(link);

const stripDiacritics = value => {
  const text = typeof value === 'string' ? value : String(value || '');
  if (typeof text.normalize === 'function') {
    return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }
  return text;
};

const toSearchString = value => stripDiacritics(String(value || '').toLowerCase());

const buildQuickAccessHaystack = link =>
  toSearchString(
    [
      link.title,
      link.subtitle,
      ...(Array.isArray(link.keywords) ? link.keywords : []),
    ]
      .filter(Boolean)
      .join(' ')
  );

const tokenize = value =>
  toSearchString(value)
    .split(/\s+/)
    .filter(Boolean);

const findQuickAccessMatches = term => {
  const tokens = tokenize(term);
  if (!tokens.length) return [];
  return QUICK_ACCESS_LINKS.filter(link => {
    const haystack = buildQuickAccessHaystack(link);
    return tokens.every(token => haystack.includes(token));
  }).map(mapQuickAccessSuggestion);
};

const buildCompanySuggestions = (items = []) =>
  items.map(item =>
    mapSuggestion({
      id: `company-${item.id}`,
      type: 'company',
      categoryLabel: 'Empresas',
      title: item.name || 'Empresa sin nombre',
      subtitle: item.location ? item.location : 'Empresa publicada',
      href: item.slug ? `/empresa/${item.slug}` : `/empresa/${item.id}`,
      icon: 'company',
    })
  );

const searchPublications = async (query, limit = DEFAULT_LIMIT_PER_TYPE) => {
  const term = String(query ?? '').trim();
  if (!term) return [];

  try {
    const { data } = await http.get('/publications', {
      params: {
        status: 'published',
        search: term,
        per_page: limit,
      },
    });
    const items = Array.isArray(data?.items) ? data.items : data;
    return (Array.isArray(items) ? items : []).map(record => {
      const companyRecord = record?.company || null;
      const companyName = companyRecord?.name || companyRecord?.display_name || '';
      const descriptor = companyName ? `Publicación · ${companyName}` : 'Publicación';
      const publicId = record.numeric_id || record.slug || record.id;
      return mapSuggestion({
        id: `publication-${record.id}`,
        type: 'publication',
        categoryLabel: 'Marketplace',
        title: record.title || record.unit_name || 'Publicación',
        subtitle: record.location || record.city || descriptor,
        href: publicId ? `/publicaciones/${publicId}` : `/publicaciones/${record.id}`,
        icon: 'publication',
      });
    });
  } catch (error) {
    console.warn('Quick search publications failed', error);
    return [];
  }
};

const searchProfiles = async (query, limit = DEFAULT_LIMIT_PER_TYPE) => {
  const term = String(query ?? '').trim().replace(/^@+/, '');
  if (!term) return [];

  try {
    const { data } = await http.get('/users', {
      params: {
        search: term,
        limit,
      },
    });
    const items = Array.isArray(data) ? data : [];
    return items.map(record => {
      const username = record.username || '';
      const subtitleParts = [];
      if (record.role) {
        subtitleParts.push(record.role === 'admin' ? 'Administrador' : record.role);
      }
      if (record.company_name) {
        subtitleParts.push(record.company_name);
      }
      const subtitle = subtitleParts.join(' · ');
      return mapSuggestion({
        id: `profile-${record.id}`,
        type: 'profile',
        categoryLabel: 'Perfiles',
        title: record.display_name || record.name || username || record.email || 'Usuario',
        subtitle: subtitle || 'Perfil público',
        href: username ? `/usuario/${username}` : `/usuario/${record.id}`,
        icon: 'profile',
      });
    });
  } catch (error) {
    console.warn('Quick search profiles failed', error);
    return [];
  }
};

const searchRequests = async (query, limit = DEFAULT_LIMIT_PER_TYPE) => {
  const term = String(query ?? '').trim();
  if (!term) return [];

  try {
    const { data } = await http.get('/service-requests', {
      params: {
        status: ['published', 'in_progress'],
        search: term,
        limit,
      },
    });
    const items = Array.isArray(data?.items) ? data.items : data;
    return (Array.isArray(items) ? items : []).map(record => {
      const companyRecord = record.company || null;
      const companyName = companyRecord?.name || '';
      const subtitleParts = [];
      if (companyName) {
        subtitleParts.push(companyName);
      }
      const pickup = record.pickup_location || record.pickupLocation;
      const dropoff = record.dropoff_location || record.dropoffLocation;
      if (pickup || dropoff) {
        subtitleParts.push([pickup, dropoff].filter(Boolean).join(' → '));
      }
      const subtitle = subtitleParts.join(' · ');
      return mapSuggestion({
        id: `request-${record.id}`,
        type: 'request',
        categoryLabel: 'Solicitudes',
        title: record.title || 'Solicitud publicada',
        subtitle: subtitle || 'Solicitud publicada',
        href: `/solicitudes/${record.id}`,
        icon: 'request',
      });
    });
  } catch (error) {
    console.warn('Quick search requests failed', error);
    return [];
  }
};

const buildDefaultQuickLinks = () =>
  DEFAULT_QUICK_ACCESS_IDS.map(id => {
    const link = QUICK_ACCESS_MAP.get(id);
    return link ? mapQuickAccessSuggestion(link) : null;
  }).filter(Boolean);

export function getQuickSearchDefaultSuggestions() {
  return buildDefaultQuickLinks().map(item => ({ ...item }));
}

export async function fetchQuickSearchSuggestions(query, options = {}) {
  const term = String(query ?? '').trim();
  const limit = Number.isFinite(options.limitPerType)
    ? Math.max(1, options.limitPerType)
    : DEFAULT_LIMIT_PER_TYPE;

  if (!term) {
    return getQuickSearchDefaultSuggestions();
  }

  const searchOption = mapSuggestion({
    id: `quick-search-${term}`,
    type: 'search',
    categoryLabel: 'Busquedas',
    title: `Buscar "${term}" en Marketplace`,
    subtitle: 'Ver resultados completos en Marketplace',
    icon: 'search',
    searchValue: term,
  });

  const quickAccessMatches = findQuickAccessMatches(term);

  const [companies, publications, profiles, requests] = await Promise.all([
    (async () => {
      try {
        const items = await searchCompaniesByName(term, { limit });
        return buildCompanySuggestions(items);
      } catch (error) {
        console.warn('Quick search companies failed', error);
        return [];
      }
    })(),
    searchPublications(term, limit),
    searchProfiles(term, limit),
    searchRequests(term, limit),
  ]);

  const suggestions = [];
  const seen = new Set();
  const push = item => {
    if (!item || !item.id || seen.has(item.id)) return;
    seen.add(item.id);
    suggestions.push(item);
  };
  const pushAll = list => {
    if (!Array.isArray(list)) return;
    list.forEach(push);
  };

  pushAll(quickAccessMatches);
  push(searchOption);
  pushAll(profiles);
  pushAll(companies);
  pushAll(publications);
  pushAll(requests);

  if (!suggestions.length) {
    push(
      mapSuggestion({
        id: 'quick-search-empty',
        type: 'empty',
        categoryLabel: 'Resultados',
        title: 'Sin coincidencias',
        subtitle: 'Intenta con otro termino de busqueda.',
        isPlaceholder: true,
      })
    );
  }

  return suggestions;
}
